import { useEffect, useState, useRef, useCallback } from "react";
import { logs } from "../lib/api";
import type { LogEntry, LogLevel, LogSource, LogStats } from "../lib/api";
import {
  RefreshCw,
  Trash2,
  Search,
  Filter,
  AlertCircle,
  AlertTriangle,
  Info,
  Bug,
  Download,
  Pause,
  Play,
} from "lucide-react";
import { format } from "date-fns";
import clsx from "clsx";

const LEVEL_ICONS: Record<LogLevel, typeof Info> = {
  debug: Bug,
  info: Info,
  warn: AlertTriangle,
  error: AlertCircle,
};

const LEVEL_COLORS: Record<LogLevel, string> = {
  debug: "text-gray-400",
  info: "text-info",
  warn: "text-warning",
  error: "text-error",
};

const SOURCE_COLORS: Record<LogSource, string> = {
  api: "bg-blue-500/20 text-blue-400",
  monitor: "bg-green-500/20 text-green-400",
  checkout: "bg-purple-500/20 text-purple-400",
  proxy: "bg-cyan-500/20 text-cyan-400",
  queue: "bg-orange-500/20 text-orange-400",
  captcha: "bg-pink-500/20 text-pink-400",
  notification: "bg-yellow-500/20 text-yellow-400",
  system: "bg-gray-500/20 text-gray-400",
};

export default function Logs() {
  const [logList, setLogList] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(true);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState<LogLevel | "">("");
  const [sourceFilter, setSourceFilter] = useState<LogSource | "">("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const wsRef = useRef<WebSocket | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Fetch logs via HTTP
  const fetchLogs = useCallback(async () => {
    try {
      const [logsData, statsData] = await Promise.all([
        logs.list({
          level: levelFilter || undefined,
          source: sourceFilter || undefined,
          search: search || undefined,
          limit: 500,
        }),
        logs.stats(),
      ]);
      setLogList(logsData.logs);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  }, [levelFilter, sourceFilter, search]);

  // Connect WebSocket for real-time logs or fall back to polling
  useEffect(() => {
    if (!streaming) return;

    let ws: WebSocket | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let isMounted = true;

    const startPolling = () => {
      // Fallback to HTTP polling if WebSocket fails
      console.log("Falling back to HTTP polling for logs");
      pollInterval = setInterval(async () => {
        if (!isMounted) return;
        try {
          const data = await logs.list({ limit: 500 });
          if (isMounted) {
            setLogList(data.logs);
            setLoading(false);
          }
        } catch (err) {
          console.error("Failed to poll logs", err);
        }
      }, 2000);
      // Also fetch immediately
      logs.list({ limit: 500 }).then((data) => {
        if (isMounted) {
          setLogList(data.logs);
          setLoading(false);
        }
      }).catch(console.error);
    };

    // Try WebSocket first
    try {
      const apiHost = import.meta.env.VITE_API_URL?.replace(/^https?:\/\//, "") || "localhost:3001";
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${wsProtocol}//${apiHost}/api/logs/stream`;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("Log stream connected");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "init") {
            setLogList(data.logs);
            setLoading(false);
          } else if (data.type === "log") {
            setLogList((prev) => {
              const newLogs = [data.log, ...prev].slice(0, 1000);
              return newLogs;
            });
          } else if (data.type === "clear") {
            setLogList([]);
          }
        } catch (e) {
          console.error("Failed to parse log message", e);
        }
      };

      ws.onerror = () => {
        console.warn("WebSocket connection failed, falling back to polling");
        if (ws) ws.close();
        startPolling();
      };

      ws.onclose = () => {
        console.log("Log stream disconnected");
      };

      wsRef.current = ws;
    } catch (err) {
      console.warn("Failed to create WebSocket, falling back to polling", err);
      startPolling();
    }

    return () => {
      isMounted = false;
      if (ws) ws.close();
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [streaming]);

  // Fetch stats periodically
  useEffect(() => {
    logs.stats().then(setStats).catch(console.error);
    const interval = setInterval(() => {
      logs.stats().then(setStats).catch(console.error);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logList, autoScroll]);

  // Filter logs client-side when streaming
  const filteredLogs = logList.filter((log) => {
    if (levelFilter && log.level !== levelFilter) return false;
    if (sourceFilter && log.source !== sourceFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        log.message.toLowerCase().includes(s) ||
        log.error?.toLowerCase().includes(s) ||
        JSON.stringify(log.data || {}).toLowerCase().includes(s)
      );
    }
    return true;
  });

  const handleClearAll = async () => {
    if (!confirm("Clear all logs?")) return;
    try {
      await logs.clear();
      setLogList([]);
      logs.stats().then(setStats);
    } catch (error) {
      console.error("Failed to clear logs", error);
    }
  };

  const handleClearOld = async () => {
    try {
      const result = await logs.clearOld(24);
      alert(`Cleared ${result.deleted} logs older than 24 hours`);
      fetchLogs();
    } catch (error) {
      console.error("Failed to clear old logs", error);
    }
  };

  const handleExport = () => {
    const data = JSON.stringify(filteredLogs, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ezpoke-logs-${format(new Date(), "yyyy-MM-dd-HHmmss")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-6 gap-3">
          <div className="card py-3">
            <p className="text-xs text-gray-400">Total Logs</p>
            <p className="text-lg font-bold">{stats.total.toLocaleString()}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Errors (24h)</p>
            <p className="text-lg font-bold text-error">{stats.errorsLast24h}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Warnings (24h)</p>
            <p className="text-lg font-bold text-warning">{stats.warningsLast24h}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Debug</p>
            <p className="text-lg font-bold text-gray-400">{stats.byLevel.debug}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Info</p>
            <p className="text-lg font-bold text-info">{stats.byLevel.info}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Stream</p>
            <p className="text-lg font-bold">
              {streaming ? (
                <span className="text-success flex items-center gap-1">
                  <span className="w-2 h-2 bg-success rounded-full animate-pulse" />
                  Live
                </span>
              ) : (
                <span className="text-gray-400">Paused</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
          <input
            type="text"
            className="w-full h-10 pl-12 pr-4 py-2 bg-dark-700 border border-dark-500 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
            placeholder="Search logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <select
            className="input w-32 h-10"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | "")}
          >
            <option value="">All Levels</option>
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
          </select>
          <select
            className="input w-36 h-10"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as LogSource | "")}
          >
            <option value="">All Sources</option>
            <option value="api">API</option>
            <option value="monitor">Monitor</option>
            <option value="checkout">Checkout</option>
            <option value="proxy">Proxy</option>
            <option value="queue">Queue</option>
            <option value="captcha">Captcha</option>
            <option value="notification">Notification</option>
            <option value="system">System</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setStreaming(!streaming)}
            className={clsx("btn-secondary", streaming && "bg-success/20 border-success/50")}
            title={streaming ? "Pause stream" : "Resume stream"}
          >
            {streaming ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={clsx("btn-secondary", autoScroll && "bg-primary-500/20 border-primary-500/50")}
            title={autoScroll ? "Disable auto-scroll" : "Enable auto-scroll"}
          >
            <RefreshCw size={16} className={autoScroll ? "animate-spin" : ""} />
          </button>
          <button onClick={handleExport} className="btn-secondary" title="Export logs">
            <Download size={16} />
          </button>
          <button onClick={handleClearOld} className="btn-secondary" title="Clear logs older than 24h">
            <Trash2 size={16} />
          </button>
          <button onClick={handleClearAll} className="btn-danger" title="Clear all logs">
            Clear All
          </button>
        </div>
      </div>

      {/* Logs List */}
      <div className="card flex-1 overflow-hidden flex flex-col">
        <div className="overflow-auto flex-1 font-mono text-sm">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="animate-spin text-primary-500" size={32} />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              No logs found
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-dark-700 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-40">Time</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-20">Level</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-28">Source</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {filteredLogs.map((log) => {
                  const Icon = LEVEL_ICONS[log.level];
                  const isExpanded = expanded.has(log.id);
                  const hasDetails = log.data || log.error || log.stack;

                  return (
                    <tr
                      key={log.id}
                      className={clsx(
                        "hover:bg-dark-700/50 cursor-pointer",
                        log.level === "error" && "bg-error/5",
                        log.level === "warn" && "bg-warning/5"
                      )}
                      onClick={() => hasDetails && toggleExpand(log.id)}
                    >
                      <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">
                        {format(new Date(log.timestamp), "HH:mm:ss.SSS")}
                      </td>
                      <td className="px-3 py-2">
                        <span className={clsx("flex items-center gap-1", LEVEL_COLORS[log.level])}>
                          <Icon size={14} />
                          <span className="text-xs uppercase">{log.level}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={clsx("status-badge text-xs", SOURCE_COLORS[log.source])}>
                          {log.source}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-2">
                          <span className={clsx("flex-1", log.level === "error" && "text-error")}>
                            {log.message}
                            {log.error && <span className="text-error ml-2">- {log.error}</span>}
                          </span>
                          {hasDetails && (
                            <span className="text-xs text-gray-500">{isExpanded ? "[-]" : "[+]"}</span>
                          )}
                        </div>
                        {isExpanded && hasDetails && (
                          <div className="mt-2 p-2 bg-dark-800 rounded text-xs overflow-auto max-h-48">
                            {log.data && (
                              <pre className="text-gray-400">{JSON.stringify(log.data, null, 2)}</pre>
                            )}
                            {log.stack && (
                              <pre className="text-error/80 mt-2 whitespace-pre-wrap">{log.stack}</pre>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>
  );
}

